/**
 * AdminHierarchy — Org-Chart-Visualisierung der Vertriebshierarchie.
 *
 * Hierarchie (top → bottom):
 *   Admin → Vertriebsdirektor → Vertriebsleiter → Vertriebler / Agent
 *
 * Kategorien rechts: Telesales-Direktoren (eigener Strang), nicht zugeordnete.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Network, RefreshCw, Loader2, Crown, Briefcase, Users, User as UserIcon,
  Headphones, AlertTriangle, ChevronRight, ChevronDown,
} from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const ROLE_LABELS = {
  admin:              { de: "Admin",              cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  sales_director:     { de: "Vertriebsdirektor",  cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  sales_manager:      { de: "Vertriebsleiter",    cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  branch_manager:     { de: "Niederlassungsleiter", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  sales_rep:          { de: "Vertriebler",        cls: "bg-zinc-700/40 text-zinc-200 border-zinc-600" },
  agent:              { de: "Telesales-Agent",    cls: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
  telesales_director: { de: "Telesales-Direktor", cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  installer:          { de: "Installateur",       cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
};

// — KPI-Strip oben
function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-xl px-3 py-2 flex items-center gap-2 min-w-0">
      <Icon size={16} className="text-zinc-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-[9.5px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
        <div className="text-[17px] font-bold text-zinc-100 font-mono leading-none">{value}</div>
      </div>
    </div>
  );
}

// — Reusable Card für Person
function PersonCard({ user, accent = "zinc", onClick, compact = false }) {
  const role = ROLE_LABELS[user.role] || { de: user.role || "—", cls: "bg-zinc-800 text-zinc-300" };
  const initials = ((user.first_name || "?")[0] + (user.last_name || "")[0]).toUpperCase();
  const accents = {
    admin:    "border-rose-500/60 bg-rose-500/5",
    director: "border-amber-500/60 bg-amber-500/5",
    manager:  "border-sky-500/60 bg-sky-500/5",
    rep:      "border-zinc-700 bg-zinc-900/60",
    agent:    "border-purple-500/60 bg-purple-500/5",
    ts_dir:   "border-fuchsia-500/60 bg-fuchsia-500/5",
    zinc:     "border-zinc-800 bg-zinc-900/40",
  };
  const cls = accents[accent] || accents.zinc;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`hier-person-${user.id}`}
      className={`text-left rounded-xl border ${cls} px-3 py-2 hover:bg-zinc-800/40 transition cursor-pointer w-full`}
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-[12px] font-bold text-zinc-200 shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-zinc-100 truncate">
            {user.first_name} {user.last_name}
          </div>
          <div className="text-[10.5px] text-zinc-500 truncate">{user.email}</div>
        </div>
        {user.is_super_admin && (
          <Crown size={11} className="text-amber-400 shrink-0" title="Super-Admin" />
        )}
      </div>
      {!compact && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-widest font-bold border ${role.cls}`}>
            {role.de}
          </span>
          {user.status === "pending" && (
            <span className="px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
              ausstehend
            </span>
          )}
        </div>
      )}
    </button>
  );
}

// — Expanding manager block
function ManagerBlock({ mgr, onPersonClick }) {
  const [open, setOpen] = useState(true);
  const teamSize = (mgr.reps?.length || 0) + (mgr.agents?.length || 0);
  return (
    <div className="space-y-2">
      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <PersonCard user={mgr} accent="manager" onClick={() => onPersonClick(mgr)} />
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={`hier-toggle-mgr-${mgr.id}`}
          className="px-2.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center"
          title={open ? "Team einklappen" : "Team ausklappen"}
        >
          {open ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
        </button>
      </div>
      {open && teamSize > 0 && (
        <div className="ml-3 pl-3 border-l-2 border-sky-500/30 space-y-1.5">
          {mgr.reps?.map((r) => (
            <PersonCard key={r.id} user={r} accent="rep" compact onClick={() => onPersonClick(r)} />
          ))}
          {mgr.agents?.map((a) => (
            <PersonCard key={a.id} user={a} accent="agent" compact onClick={() => onPersonClick(a)} />
          ))}
        </div>
      )}
      {open && teamSize === 0 && (
        <div className="ml-3 text-[10.5px] text-zinc-500 italic pl-3 border-l-2 border-zinc-800">
          Noch keine Vertriebler oder Agents zugeordnet.
        </div>
      )}
    </div>
  );
}

function DirectorBlock({ director, onPersonClick }) {
  const [open, setOpen] = useState(true);
  const teamSize = (director.managers?.length || 0) + (director.direct_agents?.length || 0);
  return (
    <div className="bg-[#18181B] border border-amber-500/20 rounded-2xl p-3.5 space-y-2.5">
      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <PersonCard user={director} accent="director" onClick={() => onPersonClick(director)} />
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={`hier-toggle-dir-${director.id}`}
          className="px-2.5 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center"
        >
          {open ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
        </button>
      </div>
      {open && teamSize === 0 && (
        <div className="text-[12px] text-zinc-300 italic ml-2">Noch keine Vertriebsleiter zugeordnet.</div>
      )}
      {open && (
        <div className="ml-3 pl-3 border-l-2 border-amber-500/30 space-y-3">
          {director.managers?.map((m) => (
            <ManagerBlock key={m.id} mgr={m} onPersonClick={onPersonClick} />
          ))}
          {director.telesales_directors?.map((td) => (
            <div key={td.id} className="space-y-1.5">
              <PersonCard user={td} accent="ts_dir" onClick={() => onPersonClick(td)} />
              {td.agents?.length > 0 && (
                <div className="ml-3 pl-3 border-l-2 border-fuchsia-500/30 space-y-1.5">
                  {td.agents.map((ag) => (
                    <PersonCard key={ag.id} user={ag} accent="agent" compact onClick={() => onPersonClick(ag)} />
                  ))}
                </div>
              )}
            </div>
          ))}
          {director.direct_agents?.map((a) => (
            <PersonCard key={a.id} user={a} accent="agent" compact onClick={() => onPersonClick(a)} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminHierarchy() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/hierarchy");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hierarchie konnte nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const onPersonClick = (u) => {
    // Direkt-Link in die Benutzerverwaltung mit Filter auf die Person
    navigate(`/admin/users?focus=${encodeURIComponent(u.id)}`);
  };

  const t = data?.totals || {};
  const showUnassigned =
    (data?.unassigned_managers?.length || 0) +
    (data?.unassigned_reps?.length || 0) +
    (data?.unassigned_agents?.length || 0) > 0;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-hierarchy-page">
      <AppHeader title="Vertriebs-Hierarchie" back="/admin/users" />
      <main className="max-w-6xl mx-auto px-3 sm:px-5 py-5 pb-12">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-2xl bg-[#00E575]/15 flex items-center justify-center">
            <Network className="text-[#00E575]" size={20} />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">Vertriebs-Hierarchie</h1>
            <p className="text-zinc-400 text-[12px] font-medium">Org-Chart von Admin bis Telesales-Agent.</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="hier-refresh"
            className="h-10 w-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40"
          >
            <RefreshCw size={14} className={loading ? "animate-spin text-zinc-400" : "text-zinc-400"} />
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
          <StatTile icon={Crown}      label="Admins"          value={t.admins ?? 0} />
          <StatTile icon={Briefcase}  label="Direktoren"      value={t.directors ?? 0} />
          <StatTile icon={Users}      label="Leiter"          value={t.managers ?? 0} />
          <StatTile icon={UserIcon}   label="Vertriebler"     value={t.reps ?? 0} />
          <StatTile icon={Headphones} label="Agents"          value={t.agents ?? 0} />
          <StatTile icon={Headphones} label="Telesales-Dir."  value={t.telesales_directors ?? 0} />
        </div>

        {loading ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex justify-center">
            <Loader2 className="animate-spin text-zinc-400" size={22} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Admin-Reihe */}
            {data?.admins?.length > 0 && (
              <section>
                <h2 className="text-[12px] uppercase tracking-widest text-rose-300 font-bold mb-2 flex items-center gap-1.5">
                  <Crown size={12} /> Administration
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.admins.map((a) => (
                    <div key={a.id} className="bg-[#18181B] border border-rose-500/20 rounded-2xl p-3 space-y-2">
                      <PersonCard user={a} accent="admin" onClick={() => onPersonClick(a)} />
                      {a.telesales_directors?.length > 0 && (
                        <div className="ml-3 pl-3 border-l-2 border-rose-500/30 space-y-2">
                          <div className="text-[9.5px] uppercase tracking-widest text-zinc-500 font-bold">Telesales-Direktoren</div>
                          {a.telesales_directors.map((td) => (
                            <div key={td.id} className="space-y-1.5">
                              <PersonCard user={td} accent="ts_dir" onClick={() => onPersonClick(td)} />
                              {td.agents?.length > 0 && (
                                <div className="ml-3 pl-3 border-l-2 border-fuchsia-500/30 space-y-1.5">
                                  {td.agents.map((ag) => (
                                    <PersonCard key={ag.id} user={ag} accent="agent" compact onClick={() => onPersonClick(ag)} />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {a.direct_agents?.length > 0 && (
                        <div className="ml-3 pl-3 border-l-2 border-rose-500/30 space-y-1.5">
                          <div className="text-[9.5px] uppercase tracking-widest text-zinc-500 font-bold">Direkt zugeordnete Agents</div>
                          {a.direct_agents.map((ag) => (
                            <PersonCard key={ag.id} user={ag} accent="agent" compact onClick={() => onPersonClick(ag)} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Vertriebs-Strang */}
            <section>
              <h2 className="text-[12px] uppercase tracking-widest text-amber-300 font-bold mb-2 flex items-center gap-1.5">
                <Briefcase size={12} /> Vertrieb
              </h2>
              {data?.directors?.length > 0 ? (
                <div className="space-y-3">
                  {data.directors.map((d) => (
                    <DirectorBlock key={d.id} director={d} onPersonClick={onPersonClick} />
                  ))}
                </div>
              ) : (
                <div className="text-[12px] text-zinc-300 italic font-medium">Keine Vertriebsdirektoren angelegt.</div>
              )}
            </section>

            {/* Telesales-Direktoren ohne Zuordnung (separater Strang) */}
            {data?.telesales_directors?.length > 0 && (
              <section>
                <h2 className="text-[12px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2 flex items-center gap-1.5">
                  <Headphones size={12} /> Telesales-Direktoren ohne Zuordnung
                </h2>
                <div className="space-y-3">
                  {data.telesales_directors.map((td) => (
                    <div key={td.id} className="bg-[#18181B] border border-fuchsia-500/20 rounded-2xl p-3.5 space-y-2">
                      <PersonCard user={td} accent="ts_dir" onClick={() => onPersonClick(td)} />
                      {td.agents?.length > 0 && (
                        <div className="ml-3 pl-3 border-l-2 border-fuchsia-500/30 space-y-1.5">
                          {td.agents.map((ag) => (
                            <PersonCard key={ag.id} user={ag} accent="agent" compact onClick={() => onPersonClick(ag)} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Nicht zugeordnete */}
            {showUnassigned && (
              <section>
                <h2 className="text-[12px] uppercase tracking-widest text-amber-300 font-bold mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={12} /> Ohne Zuordnung
                </h2>
                <div className="bg-[#18181B] border border-amber-500/20 rounded-2xl p-3 space-y-3">
                  {data.unassigned_managers?.length > 0 && (
                    <div>
                      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5">
                        Vertriebsleiter ohne Direktor
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {data.unassigned_managers.map((m) => (
                          <ManagerBlock key={m.id} mgr={m} onPersonClick={onPersonClick} />
                        ))}
                      </div>
                    </div>
                  )}
                  {data.unassigned_reps?.length > 0 && (
                    <div>
                      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5">
                        Vertriebler ohne Vorgesetzten
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {data.unassigned_reps.map((r) => (
                          <PersonCard key={r.id} user={r} accent="rep" onClick={() => onPersonClick(r)} />
                        ))}
                      </div>
                    </div>
                  )}
                  {data.unassigned_agents?.length > 0 && (
                    <div>
                      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5">
                        Telesales-Agents ohne Vorgesetzten
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {data.unassigned_agents.map((a) => (
                          <PersonCard key={a.id} user={a} accent="agent" onClick={() => onPersonClick(a)} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        <p className="text-[10.5px] text-zinc-500 mt-5 leading-relaxed">
          Klick auf eine Person öffnet die Benutzerverwaltung. Zuordnungen werden dort
          per Dropdown („Vertriebsdirektor" / „Vertriebsleiter" / „Vorgesetzter") gepflegt
          und erscheinen nach dem Speichern hier sofort beim nächsten Neuladen.
        </p>
      </main>
    </div>
  );
}
